// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Tiny, dependency-free Markdown renderer for the constrained subset the intake
// notes use (headings, bold, italics, bullet/numbered lists, line breaks). It is
// XSS-safe by construction: every value is placed as a React child (auto-escaped)
// and we never use dangerouslySetInnerHTML or render links/raw HTML.

import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

type Block =
  | { kind: 'p'; text: string }
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }

function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n?/g, '\n').split('\n')
  const blocks: Block[] = []
  let para: string[] = []
  let list: { kind: 'ul' | 'ol'; items: string[] } | null = null

  const flushPara = () => {
    if (para.length) {
      blocks.push({ kind: 'p', text: para.join('\n') })
      para = []
    }
  }
  const flushList = () => {
    if (list) {
      blocks.push(list)
      list = null
    }
  }
  const flushAll = () => {
    flushPara()
    flushList()
  }

  for (const raw of lines) {
    const line = raw.trimEnd()
    if (!line.trim()) {
      flushAll()
      continue
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line)
    if (heading) {
      flushAll()
      blocks.push({ kind: 'heading', level: (heading[1] ?? '').length, text: (heading[2] ?? '').trim() })
      continue
    }

    const ul = /^\s*[-*+]\s+(.*)$/.exec(line)
    if (ul) {
      flushPara()
      if (!list || list.kind !== 'ul') {
        flushList()
        list = { kind: 'ul', items: [] }
      }
      list.items.push((ul[1] ?? '').trim())
      continue
    }

    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(line)
    if (ol) {
      flushPara()
      if (!list || list.kind !== 'ol') {
        flushList()
        list = { kind: 'ol', items: [] }
      }
      list.items.push((ol[1] ?? '').trim())
      continue
    }

    flushList()
    para.push(line.trim())
  }
  flushAll()
  return blocks
}

// Matches **bold** / __bold__ / *italic* / _italic_ / `code` (single line only).
// NOTE: a fresh RegExp is created per call in renderSpans — the pattern is stateful
// (`g` flag → lastIndex) and renderSpans recurses, so a shared instance would have
// its lastIndex clobbered by the recursive call and re-match forever (heap blowup).
const INLINE_RE = /(\*\*[^*\n]+\*\*|__[^_\n]+__|\*[^*\n]+\*|_[^_\n]+_|`[^`\n]+`)/g

function renderSpans(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = []
  let last = 0
  let i = 0
  let m: RegExpExecArray | null
  const re = new RegExp(INLINE_RE.source, 'g')
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const tok = m[0]
    const key = `${keyPrefix}-${i++}`
    if (tok.startsWith('**') || tok.startsWith('__')) {
      out.push(
        <strong key={key} className="font-semibold text-foreground">
          {renderSpans(tok.slice(2, -2), key)}
        </strong>,
      )
    } else if (tok.startsWith('`')) {
      out.push(
        <code key={key} className="rounded bg-secondary/60 px-1 py-0.5 text-[13px]">
          {tok.slice(1, -1)}
        </code>,
      )
    } else {
      out.push(
        <em key={key} className="italic">
          {renderSpans(tok.slice(1, -1), key)}
        </em>,
      )
    }
    last = m.index + tok.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = []
  text.split('\n').forEach((seg, si) => {
    if (si > 0) out.push(<br key={`${keyPrefix}-br-${si}`} />)
    out.push(...renderSpans(seg, `${keyPrefix}-s${si}`))
  })
  return out
}

const HEADING_CLASS: Record<number, string> = {
  1: 'text-[15px] font-semibold text-foreground',
  2: 'text-[14px] font-semibold text-foreground',
}

function headingClass(level: number): string {
  return HEADING_CLASS[level] ?? 'text-[13px] font-semibold text-foreground'
}

// Renders the constrained Markdown subset used by intake notes. Styled to match
// the calm note cards (14px body, muted markers); pass className to tune spacing.
export function Markdown({ content, className }: { content: string; className?: string }) {
  const blocks = parseBlocks(content)
  if (!blocks.length) return null
  return (
    <div className={cn('space-y-2', className)}>
      {blocks.map((block, i) => {
        const key = `b${i}`
        if (block.kind === 'heading') {
          return (
            <p key={key} className={headingClass(block.level)}>
              {renderInline(block.text, key)}
            </p>
          )
        }
        if (block.kind === 'ul') {
          return (
            <ul key={key} className="list-disc space-y-1.5 pl-5 marker:text-muted-foreground">
              {block.items.map((item, j) => (
                <li key={`${key}-${j}`} className="text-[14px] leading-relaxed text-foreground">
                  {renderInline(item, `${key}-${j}`)}
                </li>
              ))}
            </ul>
          )
        }
        if (block.kind === 'ol') {
          return (
            <ol key={key} className="list-decimal space-y-1.5 pl-5 marker:text-muted-foreground">
              {block.items.map((item, j) => (
                <li key={`${key}-${j}`} className="text-[14px] leading-relaxed text-foreground">
                  {renderInline(item, `${key}-${j}`)}
                </li>
              ))}
            </ol>
          )
        }
        return (
          <p key={key} className="text-[14px] leading-relaxed text-foreground">
            {renderInline(block.text, key)}
          </p>
        )
      })}
    </div>
  )
}
